import { process, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import poDocumentColumns from 'src/assets/static/receipt_details/poDocumentColumns';
import Accordion from 'src/components/design-system/Accordion';
import Button from 'src/components/design-system/Button';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import PopUp from 'src/components/design-system/PopUp';
import GridWrapper from 'src/components/GridWrapper';
import PermissionGuard from 'src/components/guards/PermissionGuard';
import Modal from 'src/components/Modal';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import {
  BlindReceivePermissions,
  CanAccessReceipts,
  CanAccessReceiptsModule,
  CanCreateOfflinePo,
  CreateInvoicePermission,
  ThreeWayMatchingPermissions,
} from 'src/constants/permissionsList';
import usePermissions from 'src/hooks/usePermissions';
import { PoDetails } from 'src/services/usePoDetailsApi';
import { PoDocuments } from 'src/services/usePoDocumentsApi';
import { SetState } from 'src/types/generic';
import { useContextSelector } from 'use-context-selector';
import AttachDocumentContextProvider from '../attach_document/AttachDocumentContext';
import AttachDocumentWizard from '../attach_document/AttachDocumentWizard';
import { InvoiceContext } from '../invoice_details/InvoiceDetailsContext';
import { PoRelatedDocumentsContext } from './PoRelatedDocumentsContext';
import usePoRelatedDocumentsActions from './usePoRelatedDocumentsActions';

const PO_DOCUMENT_DATA_ITEM: string = 'rowId';
const initialPODocumentState: State = {
  sort: [
    {
      field: 'documentNumber',
      dir: 'asc',
    },
  ],
  take: 20,
  skip: 0,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

interface PoRelatedDocumentsProps {
  poId: string;
  poNumber: string;
  poStatus?: string;
  refreshDetails: VoidFunction;
  setLinkedDocQuantity?: SetState<number | null>;
  isInvoice: boolean;
  isDirty: boolean;
  setIsCurrentDocLinked?: SetState<boolean>;
  isEditDisabled: boolean;
  gridKey: string;
}

const PoRelatedDocuments: FC<PoRelatedDocumentsProps> = ({
  poId,
  poNumber,
  poStatus,
  refreshDetails,
  isInvoice,
  setLinkedDocQuantity,
  isDirty,
  setIsCurrentDocLinked,
  isEditDisabled,
  gridKey,
}) => {
  const [poDocumentState, setPODocumentState] = useState({ ...initialPODocumentState });
  const poDetails = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDetails);
  const poDocuments = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDocuments);
  const setPoDocuments = useContextSelector(
    PoRelatedDocumentsContext,
    (value) => value.setPoDocuments
  );
  const refresh = useContextSelector(PoRelatedDocumentsContext, (value) => value.refresh);

  const isBlindReceive = usePermissions({ permissions: BlindReceivePermissions });
  const isThreeWayMatching = usePermissions({ permissions: ThreeWayMatchingPermissions });

  const {
    buyerId = '',
    invoiceTotal = 0,
    poQty = 0,
    invoiceLineItemQty = 0,
    receiptTTLQty = 0,
  } = poDetails || ({} as PoDetails);

  const [searchParams] = useSearchParams();
  const [isNewlyLinked, setIsNewlyLinked] = useState(false);
  const [createNewDocModel, setCreateNewDocModel] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const {
    cancelSelectedDocuments,
    isCreatingInvoiceOrReceipt,
    handleCreateInvoice,
    handleCreateReceipt,
    toggleIsCreatingInvoiceOrReceipt,
    isCancelDocumentsDisabled,
    linkToSelected,
    isLinkDisabled,
    cancelDocumentsTooltipContent,
    linkTooltipContent,
    getPoDocuments,
    documentType,
    isLinkDocumentButtonLoading,
    isCancelDocumentButtonLoading,
  } = usePoRelatedDocumentsActions(
    poId,
    refreshDetails,
    isDirty,
    refresh,
    isInvoice,
    isEditDisabled,
    setIsNewlyLinked,
    setCreateNewDocModel
  );

  const receiptId = useMemo(() => searchParams.get('recid'), [searchParams]);
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);

  const hasPermissionToReceipt = usePermissions({
    permissions: CanAccessReceipts,
    or: true,
    clientId: buyerId,
  });

  const hasPermissionToInvoice = usePermissions({
    permissions: CreateInvoicePermission,
    or: true,
    clientId: buyerId,
  });

  const hasPermissionToCreateReceipt = usePermissions({ permissions: CanCreateOfflinePo });
  useEffect(() => {
    if (poDetails !== null) {
      const currentDoc = poDocuments?.find((doc) => doc?.documentId.toString() === receiptId);
      const isCurrentDocReceipt = currentDoc?.documentType === 'Receipt';
      setIsCurrentDocLinked?.(!!currentDoc?.linkedDocumentId);
      const currentLinkedDocQuantity = poDocuments?.find(
        (doc) =>
          (isCurrentDocReceipt
            ? doc?.documentType !== 'Receipt'
            : doc?.documentType === 'Receipt') && doc?.linkedDocumentId?.toString() === receiptId
      )?.documentQuantity;
      if (typeof currentLinkedDocQuantity === 'number') {
        setLinkedDocQuantity?.(currentLinkedDocQuantity);
      }
    }
  }, [poDetails, poDocuments, receiptId, setIsCurrentDocLinked, setLinkedDocQuantity]);

  const relatedDocs = useMemo(
    () => poDocuments?.map((poDocument) => ({ ...poDocument, buyerId })),
    [buyerId, poDocuments]
  );

  const { data, total } = useMemo(
    () => process(relatedDocs || [], poDocumentState) as { data: PoDocuments; total: number },
    [relatedDocs, poDocumentState]
  );

  const onPODocumentDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => setPODocumentState(e.dataState),
    []
  );

  const clearFilters = useCallback(() => {
    setPODocumentState({ ...initialPODocumentState });
    setPoDocuments((prev) => prev?.map((row) => ({ ...row, selected: false })));
  }, [setPoDocuments]);

  const onSelectionPoDocumentChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setPoDocuments((prev) =>
        [...prev].map((item) => ({
          ...item,
          selected:
            item?.rowId && item?.rowId === event.dataItem?.rowId ? !item.selected : item.selected,
        }))
      );
    },
    [setPoDocuments]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const { checked } = event.syntheticEvent.target as any;
      setPoDocuments((prev) => prev?.map((row) => ({ ...row, selected: checked })));
    },
    [setPoDocuments]
  );

  const hasErrors = useMemo(
    () =>
      poDocuments !== null &&
      isInvoice &&
      isThreeWayMatching &&
      !!poDocuments.length &&
      !poDocuments?.find((doc) => doc?.documentId?.toString() === receiptId)?.linkedDocumentId,
    [isInvoice, isThreeWayMatching, poDocuments, receiptId]
  );

  const onCancel = useCallback(() => {
    setCreateNewDocModel((prev) => !prev);
  }, []);

  const isClearFiltersDisabled = useMemo(
    () => !poDocumentState.filter?.filters.length,
    [poDocumentState.filter?.filters.length]
  );

  return (
    <>
      <Modal
        width="450px"
        height="220px"
        visible={!!createNewDocModel}
        closeModal={onCancel}
        title="Create New Document"
      >
        <p> Please select a document type to create a new record</p>
        <div className="flex justify-start items-center gap-2 py-2">
          <PermissionGuard permissions={CreateInvoicePermission} clientId={buyerId} or={true}>
            <Button onClick={handleCreateInvoice}>Invoice / Credit</Button>
          </PermissionGuard>
          <PermissionGuard permissions={CanAccessReceiptsModule} clientId={buyerId} or={false}>
            <PermissionGuard permissions={CanAccessReceipts} clientId={buyerId} or={true}>
              {hasPermissionToCreateReceipt && (
                <Button onClick={handleCreateReceipt}>Receipt</Button>
              )}
            </PermissionGuard>
          </PermissionGuard>
        </div>
        <DialogActionsBar>
          <div className="flex justify-end gap-2">
            <Button color="info" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Modal>
      <Accordion
        title={`Related Documents for PO ${poNumber || ''}  ${
          isBlindReceive
            ? ''
            : `(PO Qty: ${poQty} | ${isInvoice ? 'Invoice/Credit Total' : 'Receipt Qty'}: ${
                isInvoice ? `$${invoiceTotal}` : receiptTTLQty
              } | Variance: ${poQty - (isInvoice ? invoiceLineItemQty : receiptTTLQty)})`
        }`}
        defaultExpanded={hasErrors || isNewlyLinked}
        error={hasErrors}
        controlled
        expanded={expanded}
        setExpanded={setExpanded}
      >
        <AttachDocumentContextProvider
          isInvoice={documentType}
          poId={Number(poId)}
          refresh={getPoDocuments}
          isEnterReceipts={false}
          isNewTab
          invoiceCompanyId={invoice?.companyId?.toString()}
        >
          <AttachDocumentWizard
            openModal={isCreatingInvoiceOrReceipt}
            closeModal={toggleIsCreatingInvoiceOrReceipt}
            title={`Attach to New ${documentType ? 'Invoice' : 'Receipt'}`}
            isNewDocument={true}
            hasPoMpa={documentType && !!((poDetails?.allocations?.length || 0) > 1)}
          />
        </AttachDocumentContextProvider>
        <div className="p-3">
          <GridWrapper
            columns={poDocumentColumns}
            dataSource={data}
            dataState={poDocumentState}
            sortable
            resizable
            total={total}
            key={gridKey}
            DataItemKey={PO_DOCUMENT_DATA_ITEM}
            selectable={selectable}
            onDataStateChange={onPODocumentDataStateChange}
            pageable={gridPagerSettings}
            onSelectionChange={onSelectionPoDocumentChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            gridToolbarChildren={
              <GridToolbarChildren
                setCreateNewDocModel={setCreateNewDocModel}
                cancelSelectedDocument={cancelSelectedDocuments}
                isCancelDocumentsDisabled={isCancelDocumentsDisabled}
                isLinkDisabled={isLinkDisabled}
                linkToSelected={linkToSelected}
                poStatus={poStatus}
                clearFilters={clearFilters}
                cancelDocumentsTooltipContent={cancelDocumentsTooltipContent}
                linkTooltipContent={linkTooltipContent}
                isDirty={isDirty}
                isCreateNewDocumentDisabled={
                  !(
                    hasPermissionToInvoice ||
                    (hasPermissionToCreateReceipt && hasPermissionToReceipt)
                  )
                }
                isCancelDocumentButtonLoading={isCancelDocumentButtonLoading}
                isLinkDocumentButtonLoading={isLinkDocumentButtonLoading}
                isClearFiltersDisabled={isClearFiltersDisabled}
              />
            }
            showColumnMenu={true}
            tableTextId="Documents"
            className={'max-h-[350px]'}
          />
        </div>
      </Accordion>
    </>
  );
};

interface gridToolbarChildrenProps {
  setCreateNewDocModel: SetState<boolean>;
  cancelSelectedDocument: VoidFunction;
  linkToSelected: VoidFunction;
  isCancelDocumentsDisabled: boolean;
  isLinkDisabled: boolean;
  poStatus?: string;
  clearFilters: VoidFunction;
  linkTooltipContent: string;
  cancelDocumentsTooltipContent: string;
  isCreateNewDocumentDisabled: boolean;
  isDirty: boolean;
  isCancelDocumentButtonLoading: boolean;
  isLinkDocumentButtonLoading: boolean;
  isClearFiltersDisabled: boolean;
}

const GridToolbarChildren = ({
  setCreateNewDocModel,
  cancelSelectedDocument,
  isCancelDocumentsDisabled,
  linkToSelected,
  clearFilters,
  poStatus,
  isLinkDisabled,
  linkTooltipContent,
  cancelDocumentsTooltipContent,
  isCreateNewDocumentDisabled,
  isDirty,
  isLinkDocumentButtonLoading,
  isCancelDocumentButtonLoading,
  isClearFiltersDisabled,
}: gridToolbarChildrenProps) => {
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  return (
    <div className="flex-row gap-3">
      <ClearFilterButton onClick={clearFilters} disabled={isClearFiltersDisabled} />
      {poStatus === 'Open' && (
        <>
          <Button
            color="success"
            onClick={() => setCreateNewDocModel(true)}
            disabled={isCreateNewDocumentDisabled}
          >
            Create New Document
          </Button>
          <PopUp
            show={isLinkDisabled || isDirty}
            content={<PopUpContentWrapper contents={[linkTooltipContent]} type="Error" />}
          >
            <Button
              color="success"
              onClick={linkToSelected}
              disabled={isLinkDisabled || isDirty || isInvoiceEditDisabled}
              loading={isLinkDocumentButtonLoading}
            >
              Link to Selected
            </Button>
          </PopUp>
          <PopUp
            show={isCancelDocumentsDisabled}
            content={
              <PopUpContentWrapper contents={[cancelDocumentsTooltipContent]} type="Error" />
            }
          >
            <Button
              color="error"
              disabled={isCancelDocumentsDisabled}
              onClick={cancelSelectedDocument}
              loading={isCancelDocumentButtonLoading}
            >
              Cancel Selected Document(s)
            </Button>
          </PopUp>
        </>
      )}
    </div>
  );
};
export default PoRelatedDocuments;
